<template>
  <div class="approval-page">
    <div class="record-top">
      <div class="date-filter">
        <div class="date-row">
          <span class="date-label">时间</span>
          <span class="date-value" @click="openDateRangePicker">{{ dateRange.start || "开始日期" }}</span>
          <span class="date-sep">-</span>
          <span class="date-value" @click="openDateRangePicker">{{ dateRange.end || "结束日期" }}</span>
          <span v-if="dateRange.start || dateRange.end" class="date-clear" @click="clearDateRange">清空</span>
        </div>
        <div class="date-row">
          <span class="date-label">月份</span>
          <span class="date-value" @click="openFilterMonthPicker">{{ filterMonth || "全部月份" }}</span>
          <span v-if="filterMonth" class="date-clear" @click="clearFilterMonth">清空</span>
        </div>
        <div v-if="isSubmittedTab" class="date-row">
          <span class="date-label">剩余预算</span>
          <span class="date-value budget-value">{{ remainingBudgetDisplay }}</span>
        </div>
      </div>
      <div class="type-tabs record-filters">
        <van-tabs v-model="listType" swipeable @change="onListTypeChange">
          <van-tab v-for="opt in listTypeOptions" :key="String(opt.value)" :title="opt.label" :name="opt.value" />
        </van-tabs>
      </div>
      <div v-if="isSubmittedTab" class="record-filters">
        <van-tabs v-model="currentStatus" swipeable @change="onStatusChange">
          <van-tab v-for="tab in statusTabs" :key="String(tab.value)" :title="tab.label" :name="tab.value" />
        </van-tabs>
      </div>
    </div>

    <div class="approval-list">
      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <van-list
          v-model="loading"
          :finished="finished"
          finished-text="没有更多了"
          :error.sync="loadError"
          error-text="加载失败，点击重试"
          @load="onLoad"
        >
          <div class="records-wrap">
            <van-empty
              v-if="items.length === 0 && !loading && !loadError"
              image="search"
              :description="listEmptyText"
              class="empty"
            />
            <div
              v-for="item in items"
              :key="item.id"
              :class="['record-card', 'record-accent-card', cardStatusClass(item.status)]"
            >
              <div class="record-card-head">
                <span class="record-card-title">{{ item.createName || "-" }}</span>
                <van-tag :class="['status-tag', statusClass(item)]">{{ statusText(item) }}</van-tag>
              </div>
              <div class="record-row">
                <span class="record-label">部门</span>
                <span class="record-value">{{ item.topDepartmentName || "-" }}</span>
              </div>
              <div class="record-row">
                <span class="record-label">本次报销金额</span>
                <span class="record-value amount">{{ formatAmount(item.costAmount) }}</span>
              </div>
              <div class="record-row">
                <span class="record-label">报销日期</span>
                <span class="record-value">{{ item.applyDate || "-" }}</span>
              </div>
              <div class="record-row">
                <span class="record-label">报销月份</span>
                <span class="record-value">{{ item.month || "-" }}</span>
              </div>
              <div class="record-row">
                <span class="record-label">类型</span>
                <span class="record-value">{{ reimFormTypeText(item.type) }}</span>
              </div>
              <div class="record-row">
                <span class="record-label">创建日期</span>
                <span class="record-value">{{ item.createTime || "-" }}</span>
              </div>
              <div class="record-row" v-if="item.remark">
                <span class="record-label">备注</span>
                <span class="record-value">{{ item.remark }}</span>
              </div>

              <div class="collapse-section">
                <div class="collapse-head" @click="toggleSectionExpand(item.id, 'detail')">
                  <span class="collapse-title">报销明细</span>
                  <div class="collapse-head-right">
                    <span v-if="!isSectionExpanded(item.id, 'detail')" class="collapse-summary">
                      {{ normalizeList(item.detailList).length ? `${normalizeList(item.detailList).length} 条` : "暂无" }}
                    </span>
                    <van-icon :name="isSectionExpanded(item.id, 'detail') ? 'arrow-up' : 'arrow-down'" class="collapse-arrow" />
                  </div>
                </div>
                <div v-show="isSectionExpanded(item.id, 'detail')" class="collapse-body">
                  <div v-if="!normalizeList(item.detailList).length" class="detail-empty">暂无明细</div>
                  <div v-else>
                    <div
                      v-for="(detail, idx) in normalizeList(item.detailList)"
                      :key="`card-detail-${item.id || 'x'}-${idx}`"
                      class="detail-item-card"
                    >
                      <div class="record-row">
                        <span class="record-label">物品名称</span>
                        <span class="record-value">{{ detail.goodsName || "-" }}</span>
                      </div>
                      <div class="record-row">
                        <span class="record-label">物品价格</span>
                        <span class="record-value amount">{{ formatAmount(detail.goodsPrice) }}</span>
                      </div>
                      <div class="record-row">
                        <span class="record-label">报销类型</span>
                        <span class="record-value">{{ reimTypeText(detail.type) }}</span>
                      </div>
                      <div class="record-row">
                        <span class="record-label">项目类型</span>
                        <span class="record-value">{{ projectTypeText(detail.projectType) }}</span>
                      </div>
                      <div class="record-row" v-if="detail.remark">
                        <span class="record-label">备注</span>
                        <span class="record-value">{{ detail.remark }}</span>
                      </div>
                      <div class="record-row image-row">
                        <span class="record-label">发票截图</span>
                        <div class="image-thumb-list">
                          <img
                            v-for="(url, imgIdx) in imageUrls(detail.invoiceImage)"
                            :key="`invoice-${item.id || 'x'}-${idx}-${imgIdx}`"
                            :src="url"
                            class="image-thumb"
                            @click="previewImages(imageUrls(detail.invoiceImage), imgIdx)"
                          />
                          <span v-if="!imageUrls(detail.invoiceImage).length" class="record-value">-</span>
                        </div>
                      </div>
                      <div class="record-row image-row">
                        <span class="record-label">采购截图</span>
                        <div class="image-thumb-list">
                          <img
                            v-for="(url, imgIdx) in imageUrls(detail.purchaseImage)"
                            :key="`purchase-${item.id || 'x'}-${idx}-${imgIdx}`"
                            :src="url"
                            class="image-thumb"
                            @click="previewImages(imageUrls(detail.purchaseImage), imgIdx)"
                          />
                          <span v-if="!imageUrls(detail.purchaseImage).length" class="record-value">-</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="visibleApprovalSteps(item.approvalDetailList).length" class="collapse-section approval-detail-block">
                <div class="collapse-head" @click="toggleSectionExpand(item.id, 'approval')">
                  <span class="collapse-title">审批进度</span>
                  <div class="collapse-head-right">
                    <span v-if="!isSectionExpanded(item.id, 'approval')" class="collapse-summary">
                      {{ visibleApprovalSteps(item.approvalDetailList).length }} 人
                    </span>
                    <van-icon :name="isSectionExpanded(item.id, 'approval') ? 'arrow-up' : 'arrow-down'" class="collapse-arrow" />
                  </div>
                </div>
                <div v-show="isSectionExpanded(item.id, 'approval')" class="collapse-body">
                  <div
                    v-for="(step, stepIdx) in visibleApprovalSteps(item.approvalDetailList)"
                    :key="`approval-${item.id || 'x'}-${stepIdx}`"
                    :class="['approval-step-card', approvalStepCardClass(step.status)]"
                  >
                    <div class="record-row">
                      <span class="record-label">审批人</span>
                      <span class="record-value">{{ step.approveName || "-" }}</span>
                    </div>
                    <div class="record-row">
                      <span class="record-label">状态</span>
                      <span class="record-value" :class="approvalStepStatusClass(step.status)">
                        {{ approvalDetailStatusText(step.status) }}
                      </span>
                    </div>
                    <div class="record-row" v-if="step.approveTime">
                      <span class="record-label">审批时间</span>
                      <span class="record-value">{{ step.approveTime }}</span>
                    </div>
                    <div class="record-row" v-if="step.remark">
                      <span class="record-label">备注</span>
                      <span class="record-value">{{ step.remark }}</span>
                    </div>
                    <div v-if="shouldShowStepTransferVoucher(step)" class="record-row image-row">
                      <span class="record-label">凭证截图</span>
                      <div class="image-thumb-list">
                        <img
                          v-for="(url, imgIdx) in imageUrls(step.transferVoucher)"
                          :key="`voucher-${item.id || 'x'}-${stepIdx}-${imgIdx}`"
                          :src="url"
                          class="image-thumb"
                          @click="previewImages(imageUrls(step.transferVoucher), imgIdx)"
                        />
                        <span v-if="!imageUrls(step.transferVoucher).length" class="record-value">-</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="isSubmittedTab && Number(item.status) === 0" class="record-actions">
                <van-button
                  round
                  block
                  plain
                  type="danger"
                  :loading="cancelingId === String(item.id)"
                  @click="cancelApply(item)"
                >
                  取消申请
                </van-button>
              </div>
              <div v-if="isPendingTab && Number(item.status) === 0" class="audit-actions">
                <van-button
                  round
                  block
                  plain
                  type="danger"
                  class="action-btn action-btn-reject"
                  :loading="rejectingId === String(item.id)"
                  @click="openRejectDialog(item)"
                >
                  拒绝
                </van-button>
                <van-button
                  round
                  block
                  type="primary"
                  class="action-btn action-btn-approve"
                  :loading="approvingId === String(item.id)"
                  @click="approveItem(item)"
                >
                  同意
                </van-button>
              </div>
            </div>
          </div>
        </van-list>
      </van-pull-refresh>
    </div>

    <div v-if="isSubmittedTab" class="page-bottom-bar">
      <van-button round block type="primary" class="action-btn-approve" @click="openApplyDialog">
        申请报销
      </van-button>
    </div>

    <van-popup v-model="applyDialog.show" position="bottom" round :z-index="1100" :style="{ height: '84vh' }">
      <div class="detail-popup-inner">
        <div class="popup-head">
          <div class="popup-title">申请报销</div>
          <van-icon name="cross" class="popup-close" @click="closeApplyDialog" />
        </div>
        <div class="popup-scroll">
          <van-form @submit="submitApply">
            <van-field
              readonly
              clickable
              label="报销月份"
              :value="applyDialog.form.month"
              placeholder="请选择报销月份"
              required
              @click="applyMonthPicker.show = true"
            />
            <van-field
              readonly
              clickable
              label="类型"
              :value="applyFormTypeLabel"
              placeholder="请选择类型"
              required
              @click="applyFormTypePicker.show = true"
            />
            <van-field
              readonly
              clickable
              label="成本归属"
              :value="applyOwnerDeptLabel"
              placeholder="请选择成本归属"
              required
              @click="openOwnerDeptPicker"
            />
            <van-field
              v-model.trim="applyDialog.form.bankAccount"
              label="银行卡号"
              placeholder="请输入银行卡号"
              :required="!isApplyBankInvoiceOptional"
            />

            <div class="apply-section-title">报销明细</div>
            <div v-for="(detail, idx) in applyDialog.form.detailList" :key="`detail-${idx}`" class="detail-edit-card">
              <van-field v-model.trim="detail.goodsName" label="物品名称" placeholder="请输入" required />
              <van-field v-model.trim="detail.goodsPrice" label="物品价格" type="number" placeholder="请输入" required />
              <van-field
                readonly
                clickable
                label="报销类型"
                :value="detailTypeLabel(detail)"
                placeholder="请选择报销类型"
                required
                @click="openDetailTypePicker(idx)"
              />
              <van-field
                readonly
                clickable
                label="项目类型"
                :value="detailProjectTypeLabel(detail)"
                placeholder="请选择项目类型"
                required
                @click="openDetailProjectTypePicker(idx)"
              />
              <van-field v-model.trim="detail.remark" label="备注" type="textarea" rows="2" autosize placeholder="选填" />
              <template v-if="!isAdvanceReimApply">
                <div :class="['apply-section-subtitle', { required: !isApplyBankInvoiceOptional }]">发票截图</div>
                <div class="detail-uploader-wrap">
                  <van-uploader
                    v-model="detail.invoiceImageFiles"
                    multiple
                    :preview-options="uploaderPreviewOptions"
                    :after-read="file => onAfterReadDetailInvoiceImage(idx, file)"
                  />
                </div>
                <div class="apply-section-subtitle required">采购截图</div>
                <div class="detail-uploader-wrap">
                  <van-uploader
                    v-model="detail.purchaseImageFiles"
                    multiple
                    :preview-options="uploaderPreviewOptions"
                    :after-read="file => onAfterReadDetailPurchaseImage(idx, file)"
                  />
                </div>
              </template>
              <div class="detail-edit-actions">
                <van-button
                  v-if="applyDialog.form.detailList.length > 1"
                  size="mini"
                  plain
                  type="danger"
                  @click.prevent="removeDetailRow(idx)"
                >
                  删除
                </van-button>
              </div>
            </div>
            <div class="detail-add-wrap">
              <van-button size="small" plain type="primary" @click.prevent="addDetailRow">新增明细</van-button>
            </div>

            <van-field readonly label="本次报销金额" :value="applyCostAmountDisplay" class="field-locked">
              <template #right-icon>
                <van-icon name="lock" class="field-lock-icon" />
              </template>
            </van-field>
          </van-form>
        </div>
        <div class="popup-actions">
          <van-button round block type="primary" class="theme-btn" :loading="applyDialog.submitting" @click="submitApply">
            提交申请
          </van-button>
        </div>
      </div>
    </van-popup>

    <van-popup v-model="ownerDeptPicker.show" position="bottom" round :z-index="1120" :style="{ height: '60vh' }">
      <div class="owner-dept-popup">
        <div class="popup-head">
          <div class="popup-title">选择成本归属</div>
          <van-icon name="cross" class="popup-close" @click="ownerDeptPicker.show = false" />
        </div>
        <div class="owner-dept-scroll">
          <van-loading v-if="ownerDeptPicker.loading" class="panel-loading" size="20px" />
          <van-empty v-else-if="!ownerTopDepartmentOptions.length" description="暂无成本归属" class="panel-empty" />
          <van-checkbox-group v-else v-model="applyDialog.form.ownerTopDepartmentIds">
            <van-cell-group inset>
              <van-cell
                v-for="opt in ownerTopDepartmentOptions"
                :key="`owner-dept-${opt.value}`"
                clickable
                :title="opt.text"
                @click="toggleOwnerDept(opt.value)"
              >
                <template #right-icon>
                  <van-checkbox :name="String(opt.value)" @click.stop />
                </template>
              </van-cell>
            </van-cell-group>
          </van-checkbox-group>
        </div>
        <div class="owner-dept-actions">
          <van-button round block type="primary" class="theme-btn" @click="ownerDeptPicker.show = false">完成</van-button>
        </div>
      </div>
    </van-popup>

    <van-dialog
      v-model="rejectDialog.show"
      title="拒绝原因"
      show-cancel-button
      confirm-button-text="确认拒绝"
      cancel-button-text="取消"
      @confirm="submitReject"
    >
      <div class="dialog-body">
        <van-field
          v-model.trim="rejectDialog.reason"
          type="textarea"
          rows="3"
          autosize
          maxlength="200"
          placeholder="请输入拒绝原因（选填）"
        />
      </div>
    </van-dialog>

    <van-popup
      v-model="approveDialog.show"
      position="bottom"
      round
      :style="{ height: approveDialog.needUploadVoucher ? '84vh' : '72vh' }"
      @close="closeApproveDialog"
    >
      <div class="approve-popup">
        <div class="popup-head">
          <div class="popup-title">同意审批</div>
          <van-icon name="cross" class="popup-close" @click="closeApproveDialog" />
        </div>
        <div class="popup-scroll">
          <template v-if="approveDialog.needUploadVoucher">
            <div class="apply-section-title">补充报销凭证</div>
            <div
              v-for="(detail, idx) in approveDialog.detailList"
              :key="`approve-detail-${detail.id || idx}`"
              class="detail-edit-card"
            >
              <div class="detail-edit-card-title">{{ detail.goodsName || `明细${idx + 1}` }}</div>
              <div class="apply-section-subtitle required">发票截图</div>
              <div class="detail-uploader-wrap">
                <van-uploader
                  v-model="detail.invoiceImageFiles"
                  multiple
                  :preview-options="uploaderPreviewOptions"
                  :after-read="file => onAfterReadApproveDetailInvoice(idx, file)"
                />
              </div>
              <div class="apply-section-subtitle required">采购截图</div>
              <div class="detail-uploader-wrap">
                <van-uploader
                  v-model="detail.purchaseImageFiles"
                  multiple
                  :preview-options="uploaderPreviewOptions"
                  :after-read="file => onAfterReadApproveDetailPurchase(idx, file)"
                />
              </div>
            </div>
          </template>
          <template v-if="approveDialog.showTransferVoucher">
            <div class="apply-section-subtitle">凭证截图（选填）</div>
            <van-uploader
              v-model="approveDialog.transferVoucherFiles"
              multiple
              :preview-options="uploaderPreviewOptions"
              :after-read="onAfterReadTransferVoucher"
            />
          </template>
        </div>
        <div class="approve-footer">
          <van-button
            round
            block
            type="primary"
            class="action-btn action-btn-approve"
            :loading="approveDialog.submitting"
            @click="submitApprove"
          >
            确认同意
          </van-button>
        </div>
      </div>
    </van-popup>

    <van-calendar
      v-model="dateRangePickerShow"
      type="range"
      :allow-same-day="true"
      :show-confirm="true"
      :default-date="dateRangePickerDefault"
      :min-date="dateRangePickerMinDate"
      :max-date="dateRangePickerMaxDate"
      color="rgb(242, 124, 52)"
      @close="dateRangePickerShow = false"
      @confirm="onConfirmDateRange"
    />

    <van-popup v-model="filterMonthPicker.show" position="bottom">
      <van-datetime-picker
        v-model="filterMonthPicker.value"
        type="year-month"
        title="选择报销月份"
        :min-date="dateRangePickerMinDate"
        :max-date="dateRangePickerMaxDate"
        @cancel="filterMonthPicker.show = false"
        @confirm="onConfirmFilterMonth"
      />
    </van-popup>
    <van-popup v-model="applyMonthPicker.show" position="bottom">
      <van-datetime-picker
        v-model="applyMonthPicker.value"
        type="year-month"
        title="选择报销月份"
        :min-date="dateRangePickerMinDate"
        :max-date="dateRangePickerMaxDate"
        @cancel="applyMonthPicker.show = false"
        @confirm="onConfirmApplyMonth"
      />
    </van-popup>
    <van-popup v-model="applyFormTypePicker.show" position="bottom">
      <van-picker
        show-toolbar
        title="选择类型"
        :columns="reimFormTypeOptions"
        @cancel="applyFormTypePicker.show = false"
        @confirm="onConfirmApplyFormType"
      />
    </van-popup>
    <van-popup v-model="applyTypePicker.show" position="bottom">
      <van-picker
        show-toolbar
        title="选择报销类型"
        :columns="reimTypeOptions"
        @cancel="applyTypePicker.show = false"
        @confirm="onConfirmReimType"
      />
    </van-popup>
    <van-popup v-model="projectTypePicker.show" position="bottom">
      <van-picker
        show-toolbar
        title="选择项目类型"
        :columns="projectTypePickerColumns"
        @cancel="projectTypePicker.show = false"
        @confirm="onConfirmProjectType"
      />
    </van-popup>
  </div>
</template>

<script>
import { Toast, Dialog } from "vant"
import { previewImages as openImagePreview, UPLOADER_PREVIEW_OPTIONS } from "@/utils/imagePreview"
import { request } from "@/api/request"
import { fetchAllTopDepartments } from "@/api/customer"
import { ensureCurrentStaffProfile, getCurrentTopDepartmentId } from "@/store/currentStaff"
import { resetPageScroll, resetPageScrollAfterPaint } from "@/utils/scroll"

function fetchExpenseList(payload) {
  return request.post("/mgm-crm-api/reim/query", payload).then(r => r.data)
}

function fetchExpenseApprovalList(payload) {
  return request.post("/mgm-crm-api/reim/query-approval-list", payload).then(r => r.data)
}

function applyExpense(payload) {
  return request.post("/mgm-crm-api/reim/apply", payload).then(r => r.data)
}

function cancelExpenseApply(payload) {
  return request.post("/mgm-crm-api/reim/approve-cancel", payload).then(r => r.data)
}

function approveReimPass(payload) {
  return request.post("/mgm-crm-api/reim/approve-pass", payload).then(r => r.data)
}

function approveReimReject(payload) {
  return request.post("/mgm-crm-api/reim/approve-reject", payload).then(r => r.data)
}

function fetchReimConfig(topDepartmentId) {
  return request.get(`/mgm-crm-api/reim/config/${topDepartmentId}`).then(r => r.data)
}

function fetchReimAccountList() {
  return request.get("/mgm-crm-api/reim/account-list").then(r => r.data)
}

function uploadTosFile(file) {
  const formData = new FormData()
  formData.append("file", file)
  return request.post("/mgm-crm-api/file/tos/upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  }).then(r => r.data)
}

function normalizeList(res) {
  if (Array.isArray(res)) return res
  if (Array.isArray(res?.data)) return res.data
  if (Array.isArray(res?.list)) return res.list
  return []
}

/** 报销审批时展示凭证上传入口的员工 id（凭证选填） */
const REIM_TRANSFER_VOUCHER_STAFF_ID = "3"
/** 报销申请时银行卡号、发票截图不必填的员工 id */
const REIM_APPLY_OPTIONAL_STAFF_ID = "3"

/** 报销单类型：1常规报销 2提前预支 */
const REIM_FORM_TYPE_OPTIONS = [
  { text: "常规报销", value: 1 },
  { text: "提前预支", value: 2 }
]

function getCurrentMonthText() {
  const now = new Date()
  const p = n => String(n).padStart(2, "0")
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}`
}

function parseMonthPickerValue(v) {
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const p = n => String(n).padStart(2, "0")
    return `${v.getFullYear()}-${p(v.getMonth() + 1)}`
  }
  const txt = String(v || "").trim()
  const m = txt.match(/^(\d{4})-(\d{1,2})/)
  if (!m) return ""
  return `${m[1]}-${String(m[2]).padStart(2, "0")}`
}

function monthTextToDate(monthText) {
  const m = String(monthText || "").trim().match(/^(\d{4})-(\d{2})/)
  if (!m) return new Date()
  return new Date(Number(m[1]), Number(m[2]) - 1, 1)
}

export default {
  name: "ExpenseApproval",
  data() {
    const now = new Date()
    return {
      uploaderPreviewOptions: UPLOADER_PREVIEW_OPTIONS,
      listType: 1,
      listTypeOptions: [
        { label: "已提交", value: 1 },
        { label: "待处理", value: 2 },
        { label: "已处理", value: 3 }
      ],
      currentStatus: "all",
      statusTabs: [
        { label: "全部", value: "all" },
        { label: "审批中", value: 0 },
        { label: "已同意", value: 1 },
        { label: "已拒绝", value: 2 },
        { label: "已取消", value: 3 }
      ],
      dateRange: {
        start: "",
        end: ""
      },
      filterMonth: "",
      filterMonthPicker: {
        show: false,
        value: monthTextToDate(getCurrentMonthText())
      },
      dateRangePickerShow: false,
      dateRangePickerMinDate: new Date(1970, 0, 1),
      dateRangePickerMaxDate: new Date(2100, 11, 31),
      items: [],
      needUploadVoucher: false,
      loginTopDepartmentId: null,
      loginStaffLoading: false,
      loginStaffId: null,
      remainingBudget: null,
      budgetLoading: false,
      page: 1,
      pageSize: 20,
      loading: false,
      finished: false,
      refreshing: false,
      loadError: false,
      scrollToTopAfterLoad: false,
      cancelingId: "",
      rejectingId: "",
      approvingId: "",
      rejectDialog: { show: false, id: "", reason: "" },
      approveDialog: {
        show: false,
        id: "",
        submitting: false,
        needUploadVoucher: false,
        showTransferVoucher: false,
        transferVoucherFiles: [],
        detailList: []
      },
      sectionExpanded: {},
      applyDialog: {
        show: false,
        submitting: false,
        accountListLoading: false,
        accountList: [],
        form: {
          month: getCurrentMonthText(),
          type: 1,
          bankAccount: "",
          ownerTopDepartmentIds: [],
          detailList: [
            {
              goodsName: "",
              goodsPrice: "",
              type: null,
              projectType: null,
              remark: "",
              invoiceImageFiles: [],
              purchaseImageFiles: []
            }
          ]
        }
      },
      applyMonthPicker: {
        show: false,
        value: monthTextToDate(getCurrentMonthText())
      },
      applyFormTypePicker: { show: false },
      reimFormTypeOptions: REIM_FORM_TYPE_OPTIONS,
      applyTypePicker: { show: false },
      projectTypePicker: { show: false },
      ownerTopDepartmentOptions: [],
      ownerDeptPicker: {
        show: false,
        loading: false
      },
      detailPickerTarget: {
        index: -1,
        field: ""
      },
      reimTypeOptions: [
        { text: "市场运营", value: 1 },
        { text: "日常运营", value: 2 }
      ],
      projectTypeOptions: [
        { text: "广告/Booth点费用", value: 1, reimType: 1 },
        { text: "兼职", value: 2, reimType: 1 },
        { text: "线上运营", value: 3, reimType: 1 },
        { text: "钢琴赠送", value: 4, reimType: 1 },
        { text: "ref投诉", value: 5, reimType: 1 },
        { text: "礼品采购（所有）", value: 6, reimType: 1 },
        { text: "商场团券/课包/刷单", value: 7, reimType: 1 },
        { text: "银行还厉府各种税以及手续费", value: 8, reimType: 2 },
        { text: "网络/电话电脑/快递/差旅/财务费用", value: 9, reimType: 2 },
        { text: "完美课管等软件网站升级/买书", value: 10, reimType: 2 },
        { text: "各种保险", value: 11, reimType: 2 },
        { text: "修缮校区填补家具/调音", value: 12, reimType: 2 },
        { text: "员工关怀", value: 13, reimType: 2 },
        { text: "员工培训与创新奖励", value: 14, reimType: 2 },
        { text: "招聘内推", value: 15, reimType: 2 },
        { text: "日用品", value: 16, reimType: 2 },
        { text: "校区/外出音乐会/活动+强关联音乐会礼品采购", value: 17, reimType: 2 },
        { text: "后台比赛音乐会等活动", value: 18, reimType: 2 }
      ]
    }
  },
  computed: {
    dateRangePickerDefault() {
      const { start, end } = this.dateRange
      if (!start && !end) return []
      const s = start ? this.parseDateOnly(start) : null
      const e = end ? this.parseDateOnly(end) : null
      if (s && e) return [s, e]
      return s ? [s, s] : []
    },
    remainingBudgetDisplay() {
      if (this.budgetLoading) return "加载中..."
      const n = Number(this.remainingBudget)
      if (!Number.isFinite(n)) return "-"
      return `￥${n.toFixed(2)}`
    },
    applyCostAmount() {
      const list = Array.isArray(this.applyDialog?.form?.detailList) ? this.applyDialog.form.detailList : []
      return list.reduce((sum, row) => {
        const n = Number(row?.goodsPrice)
        return sum + (Number.isFinite(n) ? n : 0)
      }, 0)
    },
    applyCostAmountDisplay() {
      return this.formatAmount(this.applyCostAmount)
    },
    isApplyBankInvoiceOptional() {
      return String(this.loginStaffId ?? "") === REIM_APPLY_OPTIONAL_STAFF_ID
    },
    isAdvanceReimApply() {
      return Number(this.applyDialog?.form?.type) === 2
    },
    applyFormTypeLabel() {
      return this.reimFormTypeText(this.applyDialog?.form?.type)
    },
    applyOwnerDeptLabel() {
      const ids = this.applyDialog?.form?.ownerTopDepartmentIds || []
      if (!ids.length) return ""
      return ids
        .map(id => {
          const o = this.ownerTopDepartmentOptions.find(x => String(x.value) === String(id))
          return o ? o.text : String(id)
        })
        .filter(Boolean)
        .join("、")
    },
    isSubmittedTab() {
      return Number(this.listType) === 1
    },
    isPendingTab() {
      return Number(this.listType) === 2
    },
    isProcessedTab() {
      return Number(this.listType) === 3
    },
    listEmptyText() {
      if (this.isSubmittedTab) return "暂无数据"
      if (this.isPendingTab) return "暂无待处理数据"
      return "暂无已处理数据"
    },
    projectTypePickerColumns() {
      const idx = this.detailPickerTarget.index
      const detail = idx >= 0 ? this.applyDialog.form.detailList[idx] : null
      return this.getProjectTypeOptionsByReimType(detail?.type)
    }
  },
  mounted() {
    const qType = String(this.$route?.query?.type || "").trim()
    if (qType === "approval") {
      this.listType = 2
    }
    this.ensureLoginStaffInfo().then(() => this.loadRemainingBudget())
    this.onRefresh()
  },
  methods: {
    formatAmount(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return "-"
      return `￥${n.toFixed(2)}`
    },
    formatDateOnly(d) {
      const p = n => String(n).padStart(2, "0")
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    },
    monthFromDateText(v) {
      const txt = (v || "").trim()
      if (!txt) return ""
      const m = txt.match(/^(\d{4})-(\d{2})/)
      if (m) return `${m[1]}-${m[2]}`
      const d = new Date(txt.replace(/-/g, "/"))
      if (Number.isNaN(d.getTime())) return ""
      const p = n => String(n).padStart(2, "0")
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}`
    },
    parseDateOnly(v) {
      if (!v) return new Date()
      const d = new Date(String(v).replace(/-/g, "/"))
      return Number.isNaN(d.getTime()) ? new Date() : d
    },
    resolveTopDepartmentId(staff) {
      const dept = staff?.topDepartment
      if (dept === null || dept === undefined || dept === "") return null
      if (typeof dept === "object") {
        return dept.weId ?? dept.id ?? dept.topDepartmentId ?? null
      }
      return dept
    },
    async ensureLoginStaffInfo() {
      if (this.loginTopDepartmentId || this.loginStaffLoading) return !!this.loginTopDepartmentId
      this.loginStaffLoading = true
      try {
        const cachedId = getCurrentTopDepartmentId()
        if (cachedId !== null && cachedId !== undefined && cachedId !== "") {
          this.loginTopDepartmentId = cachedId
          // topDepartmentId 命中缓存时，也尽量补齐 staffId，避免审批逻辑缺失
          try {
            const staff = await ensureCurrentStaffProfile()
            this.loginStaffId = staff?.id ?? staff?.weId ?? staff?.staffId ?? this.loginStaffId
          } catch (e) {
            // ignore
          }
          return true
        }
        const staff = await ensureCurrentStaffProfile()
        this.loginStaffId = staff?.id ?? staff?.weId ?? staff?.staffId ?? this.loginStaffId
        const id = this.resolveTopDepartmentId(staff)
        if (id === null || id === undefined || id === "") return false
        this.loginTopDepartmentId = id
        return true
      } catch (e) {
        return false
      } finally {
        this.loginStaffLoading = false
      }
    },
    async ensureLoginStaffId() {
      if (this.loginStaffId !== null && this.loginStaffId !== undefined && this.loginStaffId !== "") return true
      try {
        const staff = await ensureCurrentStaffProfile()
        this.loginStaffId = staff?.id ?? staff?.weId ?? staff?.staffId ?? null
        return this.loginStaffId !== null && this.loginStaffId !== undefined && this.loginStaffId !== ""
      } catch (e) {
        return false
      }
    },
    async loadRemainingBudget(force = false) {
      if (!this.loginTopDepartmentId) return
      if (this.budgetLoading && !force) return
      this.budgetLoading = true
      try {
        const res = await fetchReimConfig(this.loginTopDepartmentId)
        const raw = res?.data ?? res
        this.remainingBudget = raw?.budget ?? null
      } finally {
        this.budgetLoading = false
      }
    },
    statusText(item) {
      const s = Number(item?.status)
      const map = { 0: "审批中", 1: "已同意", 2: "已拒绝", 3: "已取消" }
      return map[s] ?? "-"
    },
    approvalDetailStatusText(status) {
      const s = Number(status)
      if (s === 0) return "审批中"
      if (s === 1) return "已同意"
      if (s === 2) return "已拒绝"
      return "-"
    },
    approvalStepStatusClass(status) {
      const s = Number(status)
      if (s === 1) return "approval-step-approved"
      if (s === 2) return "approval-step-rejected"
      return "approval-step-pending"
    },
    approvalStepCardClass(status) {
      const s = Number(status)
      if (s === 1) return "approval-step-card-approved"
      if (s === 2) return "approval-step-card-rejected"
      return "approval-step-card-pending"
    },
    visibleApprovalSteps(list) {
      const steps = this.normalizeList(list)
      const result = []
      for (const step of steps) {
        result.push(step)
        if (Number(step?.status) === 2) break
      }
      return result
    },
    approvalStepStaffId(step) {
      const raw = step?.approveStaffId ?? step?.staffId ?? step?.approverStaffId ?? null
      return raw !== null && raw !== undefined && raw !== "" ? String(raw) : ""
    },
    shouldShowStepTransferVoucher(step) {
      if (Number(step?.status) === 0) return false
      const sid = this.approvalStepStaffId(step)
      if (sid) return sid === REIM_TRANSFER_VOUCHER_STAFF_ID
      return this.imageUrls(step?.transferVoucher).length > 0
    },
    itemSectionKey(itemId, section) {
      return `${itemId || "unknown"}-${section}`
    },
    isSectionExpanded(itemId, section) {
      return !!this.sectionExpanded[this.itemSectionKey(itemId, section)]
    },
    toggleSectionExpand(itemId, section) {
      const key = this.itemSectionKey(itemId, section)
      this.$set(this.sectionExpanded, key, !this.sectionExpanded[key])
    },
    statusClass(item) {
      const s = Number(item?.status)
      if (s === 0) return "status-pending"
      if (s === 1) return "status-approved"
      if (s === 2) return "status-rejected"
      return "status-cancel"
    },
    cardStatusClass(status) {
      const s = Number(status)
      if (s === 0) return "card-pending"
      if (s === 1) return "card-approved"
      if (s === 2) return "card-rejected"
      if (s === 3) return "card-cancel"
      return "card-pending"
    },
    approveStepText(item) {
      const cur = Number(item?.currentApproveLevel ?? item?.approveLevel ?? 0)
      const total = Number(item?.totalApproveLevel ?? item?.approveTotalLevel ?? 0)
      if (cur > 0 && total > 0) return `第${cur}/${total}层审批`
      return "-"
    },
    onListTypeChange(type) {
      this.listType = Number(type) || 1
      this.onRefresh(true)
    },
    onStatusChange(v) {
      this.currentStatus = v === "all" ? "all" : Number(v)
      this.onRefresh(true)
    },
    openDateRangePicker() {
      this.dateRangePickerShow = true
    },
    onConfirmDateRange(dates) {
      this.dateRangePickerShow = false
      const arr = Array.isArray(dates) ? dates : [dates]
      const start = arr[0]
      const end = arr[1] || arr[0]
      this.dateRange.start = start ? this.formatDateOnly(start) : ""
      this.dateRange.end = end ? this.formatDateOnly(end) : ""
      this.onRefresh()
    },
    clearDateRange() {
      if (!this.dateRange.start && !this.dateRange.end) return
      this.dateRange.start = ""
      this.dateRange.end = ""
      this.onRefresh()
    },
    openFilterMonthPicker() {
      this.filterMonthPicker.value = monthTextToDate(this.filterMonth || getCurrentMonthText())
      this.filterMonthPicker.show = true
    },
    onConfirmFilterMonth(v) {
      this.filterMonthPicker.show = false
      const month = parseMonthPickerValue(v)
      if (!month) return
      this.filterMonth = month
      this.onRefresh()
    },
    clearFilterMonth() {
      if (!this.filterMonth) return
      this.filterMonth = ""
      this.onRefresh()
    },
    onRefresh(scrollToTop = false) {
      if (scrollToTop) {
        this.scrollToTopAfterLoad = true
        resetPageScrollAfterPaint(this.$el)
      }
      this.finished = false
      this.loading = true
      this.refreshing = true
      this.page = 1
      this.items = []
      this.needUploadVoucher = false
      this.onLoad()
    },
    buildListPayload() {
      const payload = {
        createStartTime: this.dateRange.start || undefined,
        createEndTime: this.dateRange.end || undefined,
        month: (this.filterMonth || '').trim() || undefined,
        displayLength: this.pageSize,
        displayStart: this.page
      }
      const listType = Number(this.listType)
      if (listType === 1) {
        if (this.currentStatus !== "all") {
          payload.status = Number(this.currentStatus)
        }
      } else if (listType === 2) {
        payload.approvalStatusList = [0]
      } else if (listType === 3) {
        payload.approvalStatusList = [1, 2, 3]
      }
      return payload
    },
    async onLoad() {
      this.loadError = false
      try {
        const payload = this.buildListPayload()
        const listType = Number(this.listType)
        const fetchList = listType === 1 ? fetchExpenseList : fetchExpenseApprovalList
        const res = await fetchList(payload)
        if (this.refreshing || this.page === 1) {
          this.needUploadVoucher = this.isNeedUploadVoucherFlag(res?.needUploadVoucher)
        }
        const list = normalizeList(res)
        if (this.refreshing) this.items = []
        this.items = this.items.concat(list)
        this.finished = list.length < this.pageSize
        this.page += 1
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
        this.refreshing = false
        if (this.scrollToTopAfterLoad) {
          this.scrollToTopAfterLoad = false
          this.$nextTick(() => resetPageScrollAfterPaint(this.$el))
        }
      }
    },
    normalizeList(value) {
      return normalizeList(value)
    },
    async cancelApply(item) {
      const id = item?.id
      if (!id) return Toast.fail("缺少申请ID")
      try {
        await Dialog.confirm({
          message: "确定取消申请?"
        })
      } catch (e) {
        return
      }
      this.cancelingId = String(id)
      try {
        await cancelExpenseApply({ id })
        Toast.success("已取消申请")
        this.onRefresh()
      } finally {
        this.cancelingId = ""
      }
    },
    async openApplyDialog() {
      const staffOk = await this.ensureLoginStaffInfo()
      if (!staffOk || !this.loginTopDepartmentId) return Toast.fail("未获取到中心中心ID")
      await this.loadOwnerTopDepartmentOptions()
      const defaultOwnerIds = this.resolveDefaultOwnerDeptIds(this.loginTopDepartmentId)
      this.applyDialog.form = {
        month: getCurrentMonthText(),
        type: 1,
        bankAccount: "",
        ownerTopDepartmentIds: defaultOwnerIds,
        detailList: [
          {
            goodsName: "",
            goodsPrice: "",
            type: null,
            projectType: null,
            remark: "",
            invoiceImageFiles: [],
            purchaseImageFiles: []
          }
        ]
      }
      this.applyDialog.accountList = []
      this.applyMonthPicker.value = monthTextToDate(getCurrentMonthText())
      this.applyDialog.show = true
      if (!this.isApplyBankInvoiceOptional) {
        await this.loadReimAccountList()
      }
    },
    normalizeOwnerDeptOption(item) {
      if (!item || typeof item !== "object") return null
      const value = item.weId ?? item.departmentId ?? item.id
      if (value === null || value === undefined || value === "") return null
      const text = (item.weName ?? item.name ?? "").trim() || "-"
      return { text, value: String(value) }
    },
    resolveDefaultOwnerDeptIds(loginDeptId) {
      if (loginDeptId === null || loginDeptId === undefined || loginDeptId === "") return []
      const sid = String(loginDeptId)
      const hit = this.ownerTopDepartmentOptions.find(o => String(o.value) === sid)
      return hit ? [hit.value] : [sid]
    },
    async loadOwnerTopDepartmentOptions() {
      if (this.ownerDeptPicker.loading) return
      this.ownerDeptPicker.loading = true
      try {
        const res = await fetchAllTopDepartments()
        const list = normalizeList(res)
        this.ownerTopDepartmentOptions = list
          .map(item => this.normalizeOwnerDeptOption(item))
          .filter(Boolean)
      } catch (e) {
        this.ownerTopDepartmentOptions = []
        Toast.fail("成本归属列表加载失败")
      } finally {
        this.ownerDeptPicker.loading = false
      }
    },
    openOwnerDeptPicker() {
      if (this.ownerDeptPicker.loading) return Toast.fail("成本归属加载中")
      if (!this.ownerTopDepartmentOptions.length) return Toast.fail("暂无可选成本归属")
      this.ownerDeptPicker.show = true
    },
    toggleOwnerDept(value) {
      const key = String(value)
      const ids = [...(this.applyDialog.form.ownerTopDepartmentIds || [])]
      const idx = ids.findIndex(x => String(x) === key)
      if (idx >= 0) ids.splice(idx, 1)
      else ids.push(key)
      this.applyDialog.form.ownerTopDepartmentIds = ids
    },
    resolveBankAccountFromAccountItem(item) {
      if (item === null || item === undefined) return ""
      if (typeof item === "string" || typeof item === "number") return String(item).trim()
      if (typeof item === "object") {
        return String(item.bankAccount ?? item.account ?? item.cardNo ?? item.bankCardNo ?? item.accountNo ?? "").trim()
      }
      return ""
    },
    async loadReimAccountList() {
      this.applyDialog.accountListLoading = true
      try {
        const res = await fetchReimAccountList()
        const list = Array.isArray(res) ? res : normalizeList(res)
        this.applyDialog.accountList = list
        const firstAccount = list.length ? this.resolveBankAccountFromAccountItem(list[0]) : ""
        if (firstAccount) {
          this.applyDialog.form.bankAccount = firstAccount
        }
      } catch (e) {
        console.error("[报销申请] account-list 请求失败:", e)
        Toast.fail("账户列表加载失败")
      } finally {
        this.applyDialog.accountListLoading = false
      }
    },
    closeApplyDialog() {
      this.applyDialog.show = false
    },
    onConfirmApplyMonth(v) {
      const month = parseMonthPickerValue(v)
      if (month) this.applyDialog.form.month = month
      this.applyMonthPicker.show = false
    },
    onConfirmApplyFormType(values, indexes) {
      const idx = Array.isArray(indexes) ? indexes[0] : indexes
      const opt = REIM_FORM_TYPE_OPTIONS[idx]
      if (opt) this.applyDialog.form.type = opt.value
      this.applyFormTypePicker.show = false
    },
    reimFormTypeText(v) {
      const opt = REIM_FORM_TYPE_OPTIONS.find(x => Number(x.value) === Number(v))
      return opt ? opt.text : (v === null || v === undefined || v === "" ? "-" : String(v))
    },
    isNeedUploadVoucherFlag(v) {
      return v === true || v === 1 || v === "1" || v === "true"
    },
    mapServerImagesToUploaderFiles(images) {
      return this.normalizeList(images)
        .map(img => {
          const url = img?.tosUrl || (typeof img === "string" ? img : "")
          if (!url) return null
          return {
            url,
            status: "done",
            message: "已上传",
            uploadMeta: {
              name: img?.name || "",
              tosName: img?.tosName || "",
              tosUrl: url
            }
          }
        })
        .filter(Boolean)
    },
    buildApproveDetailEditList(item) {
      return this.normalizeList(item?.detailList).map((detail, idx) => ({
        id: detail.id,
        goodsName: (detail.goodsName || "").trim() || `明细${idx + 1}`,
        invoiceImageFiles: this.mapServerImagesToUploaderFiles(detail.invoiceImage),
        purchaseImageFiles: this.mapServerImagesToUploaderFiles(detail.purchaseImage)
      }))
    },
    detailTypeLabel(detail) {
      const v = detail?.type
      if (v === null || v === undefined || v === "") return ""
      const opt = this.reimTypeOptions.find(x => String(x.value) === String(v))
      return opt ? opt.text : ""
    },
    detailProjectTypeLabel(detail) {
      const v = detail?.projectType
      if (v === null || v === undefined || v === "") return ""
      const opt = this.getProjectTypeOptionsByReimType(detail?.type).find(x => String(x.value) === String(v))
      return opt ? opt.text : ""
    },
    getProjectTypeOptionsByReimType(reimType) {
      const t = Number(reimType)
      if (![1, 2].includes(t)) return []
      return this.projectTypeOptions
        .filter(x => Number(x.reimType) === t)
        .map(x => ({ text: x.text, value: x.value }))
    },
    openDetailTypePicker(index) {
      this.detailPickerTarget.index = index
      this.detailPickerTarget.field = "type"
      this.applyTypePicker.show = true
    },
    openDetailProjectTypePicker(index) {
      const detail = this.applyDialog.form.detailList[index]
      if (!detail) return
      if (detail.type === null || detail.type === undefined || detail.type === "") {
        Toast.fail("请先选择报销类型")
        return
      }
      if (!this.getProjectTypeOptionsByReimType(detail.type).length) {
        Toast.fail("当前报销类型暂无可选项目")
        return
      }
      this.detailPickerTarget.index = index
      this.detailPickerTarget.field = "projectType"
      this.projectTypePicker.show = true
    },
    onConfirmReimType(values, indexes) {
      const idx = Array.isArray(indexes) ? indexes[0] : indexes
      const opt = this.reimTypeOptions[idx]
      const targetIdx = this.detailPickerTarget.index
      if (opt && targetIdx >= 0 && this.applyDialog.form.detailList[targetIdx]) {
        const target = this.applyDialog.form.detailList[targetIdx]
        target.type = opt.value
        const allowed = this.getProjectTypeOptionsByReimType(target.type)
        if (!allowed.some(x => String(x.value) === String(target.projectType))) {
          target.projectType = null
        }
      }
      this.detailPickerTarget.index = -1
      this.detailPickerTarget.field = ""
      this.applyTypePicker.show = false
    },
    onConfirmProjectType(values, indexes) {
      const idx = Array.isArray(indexes) ? indexes[0] : indexes
      const opt = this.projectTypePickerColumns[idx]
      const targetIdx = this.detailPickerTarget.index
      if (opt && targetIdx >= 0 && this.applyDialog.form.detailList[targetIdx]) {
        this.applyDialog.form.detailList[targetIdx].projectType = opt.value
      }
      this.detailPickerTarget.index = -1
      this.detailPickerTarget.field = ""
      this.projectTypePicker.show = false
    },
    addDetailRow() {
      this.applyDialog.form.detailList.push({
        goodsName: "",
        goodsPrice: "",
        type: null,
        projectType: null,
        remark: "",
        invoiceImageFiles: [],
        purchaseImageFiles: []
      })
    },
    removeDetailRow(idx) {
      this.applyDialog.form.detailList.splice(idx, 1)
    },
    async onAfterReadDetailInvoiceImage(index, file) {
      await this.handleAfterReadDetailUpload(index, file, "invoiceImageFiles")
    },
    async onAfterReadDetailPurchaseImage(index, file) {
      await this.handleAfterReadDetailUpload(index, file, "purchaseImageFiles")
    },
    async handleAfterReadDetailUpload(index, fileOrFiles, fieldKey) {
      const detail = this.applyDialog.form.detailList[index]
      if (!detail) return
      const fileList = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]
      for (const item of fileList) {
        try {
          item.status = "uploading"
          item.message = "上传中..."
          const rawFile = item?.file
          if (!rawFile) throw new Error("文件无效")
          const uploaded = await uploadTosFile(rawFile)
          item.status = "done"
          item.message = "上传成功"
          item.url = uploaded?.tosUrl || item.url || item.content || ""
          item.uploadMeta = {
            name: uploaded?.name || rawFile?.name || "",
            tosName: uploaded?.tosName || "",
            tosUrl: uploaded?.tosUrl || ""
          }
        } catch (e) {
          item.status = "failed"
          item.message = "上传失败"
          Toast.fail("图片上传失败，请重试")
        }
      }
      detail[fieldKey] = [...(detail[fieldKey] || [])]
    },
    mapUploaderFiles(fileList) {
      const list = Array.isArray(fileList) ? fileList : []
      return list.map(file => {
        const uploaded = file?.uploadMeta
        const url = uploaded?.tosUrl || file?.url || file?.content || ""
        const name = uploaded?.name || file?.file?.name || file?.name || ""
        const tosName = uploaded?.tosName || file?.tosName || ""
        return {
          name,
          tosName,
          tosUrl: url
        }
      }).filter(x => x.tosUrl)
    },
    reimTypeText(v) {
      const opt = this.reimTypeOptions.find(x => String(x.value) === String(v))
      return opt ? opt.text : (v ?? "-")
    },
    projectTypeText(v) {
      const opt = this.projectTypeOptions.find(x => String(x.value) === String(v))
      return opt ? opt.text : (v ?? "-")
    },
    imageUrls(value) {
      return this.normalizeList(value)
        .map(x => x?.tosUrl || "")
        .filter(Boolean)
    },
    previewImages(urls, startPosition = 0) {
      openImagePreview(urls, startPosition)
    },
    validateApplyDetailList(rawList, { skipImages = false } = {}) {
      const list = Array.isArray(rawList) ? rawList : []
      if (!list.length) return "请至少添加一条报销明细"
      for (let i = 0; i < list.length; i++) {
        const row = list[i]
        const n = i + 1
        if (!(row.goodsName || "").trim()) return `第${n}条明细：请输入物品名称`
        const goodsPrice = Number(row.goodsPrice)
        if (!Number.isFinite(goodsPrice) || goodsPrice <= 0) return `第${n}条明细：请输入正确物品价格`
        if (row.type === null || row.type === undefined || row.type === "") return `第${n}条明细：请选择报销类型`
        if (row.projectType === null || row.projectType === undefined || row.projectType === "") {
          return `第${n}条明细：请选择项目类型`
        }
        if (!skipImages) {
          if (!this.mapUploaderFiles(row.invoiceImageFiles).length) {
            return `第${n}条明细：请上传发票截图`
          }
          if (!this.mapUploaderFiles(row.purchaseImageFiles).length) return `第${n}条明细：请上传采购截图`
        }
      }
      return ""
    },
    validateApproveDetailVouchers() {
      const list = Array.isArray(this.approveDialog.detailList) ? this.approveDialog.detailList : []
      if (!list.length) return "缺少报销明细"
      for (let i = 0; i < list.length; i++) {
        const row = list[i]
        const n = i + 1
        const label = row.goodsName || `第${n}条明细`
        if (!this.mapUploaderFiles(row.invoiceImageFiles).length) return `${label}：请上传发票截图`
        if (!this.mapUploaderFiles(row.purchaseImageFiles).length) return `${label}：请上传采购截图`
        const invRaw = Array.isArray(row.invoiceImageFiles) ? row.invoiceImageFiles.length : 0
        const invOk = this.mapUploaderFiles(row.invoiceImageFiles).length
        if (invRaw && invRaw !== invOk) return `${label}：存在发票截图未上传成功，请重试`
        const purRaw = Array.isArray(row.purchaseImageFiles) ? row.purchaseImageFiles.length : 0
        const purOk = this.mapUploaderFiles(row.purchaseImageFiles).length
        if (purRaw && purRaw !== purOk) return `${label}：存在采购截图未上传成功，请重试`
      }
      return ""
    },
    openRejectDialog(item) {
      const id = item?.id
      if (!id) return Toast.fail("缺少申请ID")
      this.rejectDialog.id = String(id)
      this.rejectDialog.reason = ""
      this.rejectDialog.show = true
    },
    async submitReject() {
      const id = this.rejectDialog.id
      if (!id) return Toast.fail("缺少申请ID")
      const reason = (this.rejectDialog.reason || "").trim()
      this.rejectingId = String(id)
      try {
        await approveReimReject({ id, rejectReason: reason })
        Toast.success("已拒绝")
        this.rejectDialog.show = false
        this.rejectDialog.id = ""
        this.rejectDialog.reason = ""
        this.onRefresh()
      } finally {
        this.rejectingId = ""
      }
    },
    async approveItem(item) {
      const id = item?.id
      if (!id) return Toast.fail("缺少申请ID")
      await this.ensureLoginStaffId()
      const needUploadVoucher = this.needUploadVoucher
      const needTransferVoucher = String(this.loginStaffId ?? "") === REIM_TRANSFER_VOUCHER_STAFF_ID
      if (needUploadVoucher || needTransferVoucher) {
        this.approveDialog.id = String(id)
        this.approveDialog.needUploadVoucher = needUploadVoucher
        this.approveDialog.showTransferVoucher = needTransferVoucher
        this.approveDialog.transferVoucherFiles = []
        this.approveDialog.detailList = needUploadVoucher ? this.buildApproveDetailEditList(item) : []
        this.approveDialog.show = true
        return
      }
      await this.submitApproveDirect(id)
    },
    async submitApproveDirect(id) {
      if (!id) return Toast.fail("缺少申请ID")
      if (this.approvingId) return
      this.approvingId = String(id)
      Toast.loading({ message: "提交中...", forbidClick: true, duration: 0 })
      try {
        await approveReimPass({ id })
        Toast.clear()
        Toast.success("已同意")
        this.onRefresh()
      } catch (e) {
        Toast.clear()
      } finally {
        this.approvingId = ""
      }
    },
    closeApproveDialog() {
      if (this.approveDialog.submitting) return
      this.approveDialog.show = false
      this.approveDialog.id = ""
      this.approveDialog.needUploadVoucher = false
      this.approveDialog.showTransferVoucher = false
      this.approveDialog.transferVoucherFiles = []
      this.approveDialog.detailList = []
    },
    async onAfterReadApproveDetailInvoice(index, file) {
      await this.handleAfterReadApproveDetailUpload(index, file, "invoiceImageFiles")
    },
    async onAfterReadApproveDetailPurchase(index, file) {
      await this.handleAfterReadApproveDetailUpload(index, file, "purchaseImageFiles")
    },
    async handleAfterReadApproveDetailUpload(index, fileOrFiles, fieldKey) {
      const detail = this.approveDialog.detailList[index]
      if (!detail) return
      const fileList = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]
      for (const item of fileList) {
        try {
          item.status = "uploading"
          item.message = "上传中..."
          const rawFile = item?.file
          if (!rawFile) throw new Error("文件无效")
          const uploaded = await uploadTosFile(rawFile)
          item.status = "done"
          item.message = "上传成功"
          item.url = uploaded?.tosUrl || item.url || item.content || ""
          item.uploadMeta = {
            name: uploaded?.name || rawFile?.name || "",
            tosName: uploaded?.tosName || "",
            tosUrl: uploaded?.tosUrl || ""
          }
        } catch (e) {
          item.status = "failed"
          item.message = "上传失败"
          Toast.fail("图片上传失败，请重试")
        }
      }
      detail[fieldKey] = [...(detail[fieldKey] || [])]
    },
    async onAfterReadTransferVoucher(fileOrFiles) {
      const fileList = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]
      for (const item of fileList) {
        try {
          item.status = "uploading"
          item.message = "上传中..."
          const rawFile = item?.file
          if (!rawFile) throw new Error("文件无效")
          const uploaded = await uploadTosFile(rawFile)
          item.status = "done"
          item.message = "上传成功"
          item.url = uploaded?.tosUrl || item.url || item.content || ""
          item.uploadMeta = {
            name: uploaded?.name || rawFile?.name || "",
            tosName: uploaded?.tosName || "",
            tosUrl: uploaded?.tosUrl || ""
          }
        } catch (e) {
          item.status = "failed"
          item.message = "上传失败"
          Toast.fail("图片上传失败，请重试")
        }
      }
      this.approveDialog.transferVoucherFiles = [...(this.approveDialog.transferVoucherFiles || [])]
    },
    mapAuditUploaderFiles(fileList) {
      const list = Array.isArray(fileList) ? fileList : []
      return list
        .map(file => {
          const uploaded = file?.uploadMeta
          const url = uploaded?.tosUrl || file?.url || file?.content || ""
          const name = uploaded?.name || file?.file?.name || file?.name || ""
          const tosName = uploaded?.tosName || file?.tosName || ""
          return { name, tosName, tosUrl: url }
        })
        .filter(x => x.tosUrl)
    },
    async submitApprove() {
      if (this.approveDialog.submitting) return
      const id = this.approveDialog.id
      if (!id) return Toast.fail("缺少申请ID")
      const transferVoucher = this.mapAuditUploaderFiles(this.approveDialog.transferVoucherFiles)
      const rawTransferCount = Array.isArray(this.approveDialog.transferVoucherFiles)
        ? this.approveDialog.transferVoucherFiles.length
        : 0
      if (rawTransferCount && transferVoucher.length !== rawTransferCount) {
        return Toast.fail("存在凭证截图未上传成功，请重试")
      }
      let detailList
      if (this.approveDialog.needUploadVoucher) {
        const voucherErr = this.validateApproveDetailVouchers()
        if (voucherErr) return Toast.fail(voucherErr)
        detailList = this.approveDialog.detailList.map(row => ({
          id: row.id,
          invoiceImage: this.mapUploaderFiles(row.invoiceImageFiles),
          purchaseImage: this.mapUploaderFiles(row.purchaseImageFiles)
        }))
      }

      this.approveDialog.submitting = true
      this.approvingId = String(id)
      Toast.loading({ message: "提交中...", forbidClick: true, duration: 0 })
      try {
        const payload = { id }
        if (rawTransferCount) payload.transferVoucher = transferVoucher
        if (detailList) payload.detailList = detailList
        await approveReimPass(payload)
        Toast.clear()
        Toast.success("已同意")
        this.approveDialog.show = false
        this.approveDialog.id = ""
        this.approveDialog.needUploadVoucher = false
        this.approveDialog.showTransferVoucher = false
        this.approveDialog.transferVoucherFiles = []
        this.approveDialog.detailList = []
        this.onRefresh()
      } catch (e) {
        Toast.clear()
      } finally {
        Toast.clear()
        this.approveDialog.submitting = false
        this.approvingId = ""
      }
    },
    async submitApply() {
      if (this.applyDialog.submitting) return
      const staffOk = await this.ensureLoginStaffInfo()
      if (!staffOk || !this.loginTopDepartmentId) return Toast.fail("未获取到中心中心ID")
      const form = this.applyDialog.form
      const bankOptional = this.isApplyBankInvoiceOptional
      const skipImages = this.isAdvanceReimApply
      const bankAccount = (form.bankAccount || "").trim()
      if (!bankOptional && !bankAccount) return Toast.fail("请输入银行卡号")
      const month = (form.month || "").trim()
      if (!month) return Toast.fail("请选择报销月份")
      const ownerTopDepartmentIds = (Array.isArray(form.ownerTopDepartmentIds) ? form.ownerTopDepartmentIds : [])
        .map(x => String(x).trim())
        .filter(Boolean)
      if (!ownerTopDepartmentIds.length) return Toast.fail("请选择成本归属")
      const rawDetailList = Array.isArray(form.detailList) ? form.detailList : []
      const detailValidateMsg = this.validateApplyDetailList(rawDetailList, { skipImages })
      if (detailValidateMsg) return Toast.fail(detailValidateMsg)
      const costAmount = this.applyCostAmount
      if (!Number.isFinite(costAmount) || costAmount <= 0) return Toast.fail("报销明细金额需大于 0")
      const detailList = rawDetailList.map(row => ({
        goodsName: (row.goodsName || "").trim(),
        goodsPrice: Number(row.goodsPrice),
        invoiceImage: skipImages ? [] : this.mapUploaderFiles(row.invoiceImageFiles),
        projectType: row.projectType,
        purchaseImage: skipImages ? [] : this.mapUploaderFiles(row.purchaseImageFiles),
        remark: (row.remark || "").trim() || undefined,
        type: row.type
      }))
      const uploadBroken = skipImages
        ? false
        : rawDetailList.some(row => {
        const invRaw = Array.isArray(row.invoiceImageFiles) ? row.invoiceImageFiles.length : 0
        const invOk = this.mapUploaderFiles(row.invoiceImageFiles).length
        if (invRaw && invRaw !== invOk) return true
        const purRaw = Array.isArray(row.purchaseImageFiles) ? row.purchaseImageFiles.length : 0
        const purOk = this.mapUploaderFiles(row.purchaseImageFiles).length
        return purRaw && purRaw !== purOk
      })
      if (uploadBroken) {
        return Toast.fail("存在图片未上传成功，请重试")
      }
      const payload = {
        bankAccount: bankAccount || undefined,
        costAmount,
        detailList,
        ownerTopDepartmentId: ownerTopDepartmentIds,
        topDepartmentId: this.loginTopDepartmentId,
        month,
        type: Number(form.type) || 1
      }
      this.applyDialog.submitting = true
      Toast.loading({ message: "提交中...", forbidClick: true, duration: 0 })
      try {
        await applyExpense(payload)
        Toast.clear()
        Toast.success("申请成功")
        this.applyDialog.show = false
        this.onRefresh()
        await this.loadRemainingBudget(true)
      } finally {
        this.applyDialog.submitting = false
      }
    }
  }
}
</script>

<style src="@/styles/approval-status.css"></style>
<style scoped>
.approval-page {
  min-height: 100vh;
  background: #f7f8fa;
  display: flex;
  flex-direction: column;
}

.record-top {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  padding-top: 4px;
}

.date-filter {
  padding: 0 12px 4px;
  background: #fff;
}

.date-row {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 2px;
}

.date-label {
  font-size: 13px;
  color: #969799;
  flex: 0 0 auto;
}

.date-value {
  font-size: 13px;
  color: #323233;
  padding: 1px 5px;
  border-radius: 6px;
  background: #f7f8fa;
}

.date-sep {
  color: #969799;
  flex: 0 0 auto;
}

.date-clear {
  margin-left: 6px;
  font-size: 12px;
  color: rgb(242, 124, 52);
}

.record-filters {
  background: #fff;
}

:deep(.van-tab--active) {
  color: rgb(242, 124, 52);
}

:deep(.van-tabs__line) {
  background: rgb(242, 124, 52);
  background-color: rgb(242, 124, 52);
}

:deep(.van-tabs__wrap) {
  height: 34px;
}

.approval-list {
  flex: 1;
  padding: 8px 12px calc(80px + var(--app-safe-area-bottom, 0px));
}

.page-bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: var(--app-tabbar-offset, 50px);
  padding: 10px 12px;
  background: #fff;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
  z-index: 90;
}

.records-wrap {
  margin-top: 2px;
}

.record-card {
  background: #fff;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(30, 35, 90, 0.05);
}

.record-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.record-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #323233;
}

.record-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-top: 6px;
  gap: 12px;
}

.record-row.image-row {
  display: block;
}

.record-row.image-row .record-label {
  display: block;
  margin-bottom: 6px;
}

.record-actions {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.record-label {
  color: #969799;
}

.record-value {
  color: #323233;
  text-align: right;
  word-break: break-all;
}

.amount {
  color: rgb(242, 124, 52);
  font-weight: 700;
}

.detail-popup-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.popup-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #ebedf0;
}

.popup-title {
  font-size: 16px;
  font-weight: 600;
}

.popup-close {
  font-size: 18px;
  color: #969799;
}

.popup-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px;
}

.detail-kv {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 14px;
}

.detail-kv-label {
  color: #969799;
  margin-right: 12px;
}

.detail-kv-value {
  color: #323233;
  text-align: right;
  word-break: break-all;
}

.detail-section-title {
  margin-top: 12px;
  font-size: 13px;
  color: #969799;
}

.detail-empty {
  margin-top: 6px;
  font-size: 12px;
  color: #969799;
}

.detail-item-card {
  margin-top: 8px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 0 10px 10px;
}

.collapse-section {
  margin-top: 10px;
  border-top: 1px dashed #f0f0f0;
  padding-top: 8px;
}

.collapse-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.collapse-title {
  font-size: 13px;
  color: #323233;
  font-weight: 600;
  flex-shrink: 0;
}

.collapse-head-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.collapse-summary {
  font-size: 12px;
  color: #969799;
  text-align: right;
}

.collapse-arrow {
  font-size: 14px;
  color: #969799;
  flex-shrink: 0;
}

.collapse-body {
  margin-top: 8px;
}

.approval-detail-block {
  margin-top: 0;
}

.approval-step-card {
  margin-top: 8px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 0 10px 10px;
  background: #fff;
}

.approval-step-card-pending {
  background: rgba(245, 158, 11, 0.06);
  border-color: rgba(245, 158, 11, 0.28);
}

.approval-step-card-approved {
  background: rgba(34, 160, 107, 0.06);
  border-color: rgba(34, 160, 107, 0.28);
}

.approval-step-card-rejected {
  background: rgba(214, 93, 140, 0.06);
  border-color: rgba(214, 93, 140, 0.28);
}

.approval-step-pending {
  color: #f59e0b;
  font-weight: 600;
}

.approval-step-approved {
  color: #22a06b;
  font-weight: 600;
}

.approval-step-rejected {
  color: #d65d8c;
  font-weight: 600;
}

.image-thumb-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
}

.image-thumb {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.popup-actions {
  flex-shrink: 0;
  display: flex;
  gap: 12px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  border-top: 1px solid #ebedf0;
  background: #fff;
}

.action-btn {
  flex: 1;
  height: 42px;
}

.action-btn-approve {
  color: #fff;
  background: rgb(242, 124, 52);
  border-color: rgb(242, 124, 52);
}

.apply-section-title {
  margin: 10px 0 8px;
  font-size: 13px;
  color: #969799;
}

.apply-section-subtitle {
  position: relative;
  margin: 0;
  padding: 10px 16px 6px;
  font-size: 14px;
  line-height: 24px;
  color: #646566;
}

.apply-section-subtitle.required::before {
  position: absolute;
  left: 8px;
  color: #ee0a24;
  font-size: 14px;
  line-height: 24px;
  content: "*";
}

.detail-uploader-wrap {
  padding: 0 16px 10px;
}

.detail-edit-card {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 8px;
}

.detail-edit-card-title {
  padding: 4px 6px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #323233;
}

.detail-edit-actions {
  display: flex;
  justify-content: flex-end;
  padding: 0 2px 8px;
}

.detail-add-wrap {
  margin-bottom: 8px;
}

.owner-dept-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.owner-dept-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0 12px;
}

.owner-dept-actions {
  flex-shrink: 0;
  padding: 10px 16px calc(10px + var(--app-safe-area-bottom, 0px));
  border-top: 1px solid #ebedf0;
  background: #fff;
}

.theme-btn {
  background: rgb(242, 124, 52) !important;
  border-color: rgb(242, 124, 52) !important;
  color: #fff !important;
}

.panel-loading {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

:deep(.detail-edit-card .van-field.van-cell--required) {
  padding-left: 16px;
}

.audit-actions {
  display: flex;
  gap: 10px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.action-btn {
  flex: 1;
  height: 40px;
}

.action-btn-reject {
  color: #ee0a24;
  border-color: #ee0a24;
}

.approve-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.popup-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #ebedf0;
}

.popup-title {
  font-size: 16px;
  font-weight: 600;
}

.popup-close {
  font-size: 18px;
  color: #969799;
}

.popup-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px;
}

.popup-tip {
  font-size: 13px;
  color: #969799;
  margin-bottom: 10px;
}

.apply-section-subtitle {
  margin: 8px 0 6px;
  font-size: 13px;
  color: #646566;
}

.approve-footer {
  border-top: 1px solid #ebedf0;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  background: #fff;
}

.dialog-body {
  padding: 10px 12px 0;
}

.empty {
  margin-top: 60px;
}
</style>
